import React, { useEffect, useState } from "react";
import "./ResidentTheatre.css";

const SEAT_ROWS = [
  { row: "A", count: 8 },
  { row: "B", count: 8 },
  { row: "C", count: 7 },
];

export default function ResidentTheatre() {
  const [shows, setShows] = useState([]);
  const [activeShow, setActiveShow] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const API = process.env.REACT_APP_API_URL;
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("residentToken")}` };
  const residentId = JSON.parse(atob((localStorage.getItem("residentToken") || "").split(".")[1] || "e30=") || "{}").id;

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/theatre/shows`, { headers: authHeader });
      if (!res.ok) {
        setError("Failed to load shows");
        return;
      }
      const data = await res.json();
      setShows(data);
      if (activeShow) {
        const refreshed = data.find((s) => s.id === activeShow.id);
        setActiveShow(refreshed || null);
      }
    } catch (err) {
      setError("Server error while loading shows");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShow = (show) => {
    setActiveShow(show);
    setSelectedSeats([]);
    setError("");
    setSuccess("");
  };

  const toggleSeat = (seat) => {
    if (seat.status === "booked") return;
    setSelectedSeats((prev) =>
      prev.includes(seat.seatId) ? prev.filter((s) => s !== seat.seatId) : [...prev, seat.seatId]
    );
  };

  const bookSeats = async () => {
    setError("");
    setSuccess("");
    if (selectedSeats.length === 0) {
      setError("Select at least one seat");
      return;
    }

    try {
      const res = await fetch(`${API}/api/theatre/shows/${activeShow.id}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ seatIds: selectedSeats }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to book seats");
      }

      setActiveShow(data);
      setSelectedSeats([]);
      setSuccess("Seats booked!");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const cancelSeat = async (seatId) => {
    try {
      const res = await fetch(`${API}/api/theatre/shows/${activeShow.id}/cancel-seat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ seatId }),
      });
      const data = await res.json();
      if (res.ok) {
        setActiveShow(data);
        load();
      }
    } catch (err) {
      setError("Failed to cancel seat");
    }
  };

  const formatTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
  };

  return (
    <div className="resident-layout">
      
      <div className="resident-content">
        <h1 className="theatre-title">Mini Theatre</h1>

        <div className="theatre-info-banner">
          ₹3,000 + 18% GST per slot booking · Booking hours: 10:00 AM – 10:00 PM · Each slot is 3.5 hours · Seats 23
        </div>

        {error && <p className="theatre-error">{error}</p>}
        {success && <p className="theatre-success">{success}</p>}

        {!activeShow && (
          <div className="theatre-show-list">
            {shows.map((s) => {
              const bookedCount = s.seats.filter((seat) => seat.status === "booked").length;
              return (
                <div className="theatre-show-card" key={s.id} onClick={() => openShow(s)}>
                  <div className="theatre-show-name">{s.movieName}</div>
                  <div className="theatre-show-meta">
                    {s.showDate} · {formatTime(s.startTime)} – {formatTime(s.endTime)}
                  </div>
                  <div className="theatre-show-seats">{bookedCount} / 23 seats booked</div>
                </div>
              );
            })}

            {shows.length === 0 && !error && (
              <p className="theatre-empty">No shows published yet — check back soon.</p>
            )}
          </div>
        )}

        {activeShow && (
          <div className="theatre-booking-panel">
            <button className="theatre-back-btn" onClick={() => setActiveShow(null)}>← Back to shows</button>

            <h2 className="theatre-show-title">{activeShow.movieName}</h2>
            <p className="theatre-show-subtitle">
              {activeShow.showDate} · {formatTime(activeShow.startTime)} – {formatTime(activeShow.endTime)}
            </p>

            <div className="theatre-screen">SCREEN</div>

            <div className="theatre-seat-map">
              {SEAT_ROWS.map(({ row }) => (
                <div className="theatre-row" key={row}>
                  <span className="theatre-row-label">{row}</span>
                  <div className="theatre-row-seats">
                    {activeShow.seats
                      .filter((s) => s.seatId.startsWith(row))
                      .map((seat) => {
                        const isMine = seat.bookedBy && seat.bookedBy.residentId === residentId;
                        const isSelected = selectedSeats.includes(seat.seatId);
                        let cls = "theatre-seat";
                        if (seat.status === "booked") cls += isMine ? " mine" : " booked";
                        else if (isSelected) cls += " selected";
                        return (
                          <button
                            key={seat.seatId}
                            className={cls}
                            disabled={seat.status === "booked" && !isMine}
                            onClick={() => (isMine ? cancelSeat(seat.seatId) : toggleSeat(seat))}
                            title={isMine ? "Your seat — click to cancel" : seat.status === "booked" ? "Booked" : seat.seatId}
                          >
                            {seat.seatId.slice(1)}
                          </button>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>

            <div className="theatre-legend">
              <span><i className="theatre-seat available"></i> Available</span>
              <span><i className="theatre-seat selected"></i> Selected</span>
              <span><i className="theatre-seat booked"></i> Booked</span>
              <span><i className="theatre-seat mine"></i> Your seat</span>
            </div>

            {selectedSeats.length > 0 && (
              <button className="theatre-book-btn" onClick={bookSeats}>
                Book {selectedSeats.length} Seat{selectedSeats.length > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}